/**
 * Simplified Chinese (`zh`) string catalogue — mirrors the key shape of
 * `./en.ts`. Brand names (`OVERVOLT`, `Sniper Rail`, `Arc Coil`, `Cryo
 * Emitter`, `Splash Mortar`, `Pulse Turret`, `Pylon`, `Flux`) stay in
 * English; descriptive copy translates into terse, in-game Simplified
 * Chinese using standard tower-defense terminology (波次 / 升级 / 解锁 /
 * 难度 etc.).
 */

import type { Catalogue } from './en';

export const zh: Catalogue = {
  meta: {
    name: '简体中文',
    code: 'ZH',
  },

  towers: {
    pulse: {
      label: 'Pulse Turret',
      short: '脉冲',
      role: '中立通用型。路径任意位置稳定输出。',
    },
    arc: {
      label: 'Arc Coil',
      short: '电弧',
      role: '与电网耦合。Pylon 富余时更强,断电时变弱。',
    },
    cryo: {
      label: 'Cryo Emitter',
      short: '冰冻',
      role: '咽喉画笔。范围小 — 在一个关卡点减速并标记每个敌人。',
    },
    mortar: {
      label: 'Splash Mortar',
      short: '迫击',
      role: '间瞄火力。远离路径放置 — 近距是它的盲区。',
    },
    sniper: {
      label: 'Sniper Rail',
      short: '狙击',
      role: '直线穿透。长直道强力;弯角处弱。',
    },
    pylon: {
      label: 'Pylon',
      short: 'PYLON',
      role: '供电。建够覆盖塔的需求(给电弧留一点富余)。',
    },
    wall: {
      label: 'Wall Node',
      short: '墙',
      role: '便宜阻挡 — 仅迷宫模式。塑造敌人必走路线。',
    },
  },

  roleHints: {
    pulse: 'Pulse Turret:稳定的首选 — 路径任意位置都能输出。',
    arc: 'Arc Coil:随电力富余成长。多建 Pylon 让链条更强。',
    cryo: 'Cryo Emitter:精准画咽喉。在挤压点减速并标记几格。',
    mortar: 'Splash Mortar:间瞄火力。远离路径放置 — 近距打不到。',
    sniper: 'Sniper Rail:直线穿透。对齐长直道效果最大。',
    pylon: 'Power Pylon:为电网供电。建够覆盖塔的需求。',
    wall: '墙:便宜阻挡 — 塑造敌人必走的路线。',
  },

  hints: {
    build: '建塔:在下方选一个(或按 1),再点击路径旁发光的格子。',
    placed: '不错。再建一个,按空格发起波次。',
    wave1: '塔会自动开火。注意电力条不要进入红色区域。',
    select: '点击已建造的塔进行选中 — U 升级,S 出售。',
    brownout: '断电!塔已减速。建造 Pylon(按 3)以补充电力。',
  },

  contextHints: {
    overload:
      '过载:提高下一波以获得更多奖励 — 风险更高。开局慢?调高它换更快的 Flux。',
    profileKey: '按 P 打开档案 — Flux 解锁新塔、技能和模式。',
    endlessMod: '无尽模式轮换波次修饰符 — 调整你的构筑。',
    puzzleHover:
      '解谜模式:手工设计的逻辑关卡,带视野遮挡墙。完全不同的玩法。',
  },

  hud: {
    buildZone: '建造',
    waveZone: '波次',
    abilitiesZone: '技能',
    overloadZone: '过载',

    startWave: '开始波次',
    nextWave: '下一波',
    pause: '暂停',
    resume: '继续',
    restart: '重来',
    deployHint: '出击 [SPACE]',
    holdHint: '暂停 [SPACE]',
    rebootHint: '重启 [R]',
    nextWaveHint: '下一波',

    audioOn: '音效 开',
    audioOff: '音效 关',

    credits: '点数',
    core: '核心',
    wave: '波次',
    endless: '无尽',
    score: '得分',
    powerGrid: '电网',

    runMode: '模式',
    difficulty: '难度',
    profile: '档案',
    map: '地图',
    clickHint: '▷ 点击',

    locked: '已锁',
    lockedShort: '已锁',
    lockedTooltip: '已锁 — 在档案中花 ✦{cost} Flux 解锁(按 P)。',
    lockedTowerNotice: '{tower} 已锁',
    lockedAbility: '技能已锁',
    lockedOverload: '过载等级已锁',
    lockedMode: '{mode} 模式已锁',
    lockedDifficulty: '困难难度已锁',

    cheatPip: '🔓 作弊',
    profilePip: '👤 {name}',

    overloadIdle: '— 过载从第 1 波生效 —',
    overloadTier: 'TIER {tier} · {name}',
    overloadActive: '◉ 已激活 {sec}s',

    maxLevel: '满级',
    upgrade: '升级 L{level}',
    sell: '出售',
    target: '目标:{mode}',
    cycleTarget: '切换 · [T]',
    sellHint: '+{refund}c · [S]',
    upgradeHint: '{cost}c · [U]',
    overclock: '超频',
    overclockOn: '超频 ✦ 开',

    // Sprint 69 (i18n-gaps-extract-s69) — extracted from render.ts / hud.ts.
    buildHintIntro: "建造阶段  //  选一座塔 [1·2·3·4·5]，放下后 START WAVE [SPACE]",
    buildHintReinforce: "建造阶段  //  加固电网，然后 START WAVE {wave} [SPACE]",
    mazeRoute: "迷 宫 路 径",
    tiles: "格",
    abilityActive: "◉ 激活 {sec}s",
    costSuffix: '{cost}c',
    overclockPip: 'OC ×{n}',
    powerReadout: '{demand} / {supply}W  ·  {pct}%',
    towerCostDraw: '{cost}c {power}w',
    pylonCostSupply: '{cost}c +{supply}w',
    wallCost: '{cost}c 0w',
    endlessModBadge: 'MOD · {label}',
    startWaveOverload: '⚡{n} {tierName}',
    waveOverloadPip: '⚡{n} {tierName}',
    overclockNoticeOn: "{tower} 超频 开",
    overclockNoticeOff: "{tower} 超频 关",

    tierMult: {
      hp: "生",
      sp: "速",
      cn: "数",
      gd: "金",
      sc: "分",
    },
    spawnShort: {
      bit: "Bit",
      packet: "Pkt",
      daemon: "Dmn",
      fork: "Fork",
      cipher: "Cph",
      patch: "Pch",
      buffer: "Buf",
      streak: "Stk",
      kernel: "内核",
    },
  },

  modes: {
    campaign: '战役',
    endless: '无尽',
    maze: '迷宫',
    puzzle: '解谜',
  },
  modeSub: {
    campaign: '10 波 · 通关',
    endless: '✦ {cost}',
    maze: '✦ {cost}',
    puzzle: '选关卡',
  },
  modeBlurb: {
    campaign: '10 波。第 10 波 BOSS。打赢即毕业。',
    endless: '同一张图。每波都换修正词。会变阵,不然就漏。',
    maze: '开阔场地。你用墙塑造路径。拖慢他们或榨干他们的赏金。',
    puzzle: '10 个手工关卡。找出唯一获胜布局。',
  },
  modeCard: {
    campaign: {
      tagline: '学会电网,打趴 BOSS。',
      bullet1: '10 波手工调校 + BOSS',
      bullet2: '固定 S 形路径 — 建造、波次、升级',
      bullet3: '通关解锁本图的 Endless',
      length: '约 8 分钟',
      skill: '锻炼建造顺序',
    },
    endless: {
      tagline: '会变阵,不然就漏。永无止境。',
      bullet1: '先打 Campaign 1–10,之后无尽进阶',
      bullet2: '每波刷新一个修正词',
      bullet3: '核心被破即结束',
      length: '约 30 分钟',
      skill: '锻炼长线转型',
    },
    maze: {
      tagline: '你来搭他们必走的迷宫。',
      bullet1: '开阔场地 — 没有预设路径',
      bullet2: '塔与廉价墙塑造路线',
      bullet3: '路线越长,火力时间越久',
      length: '约 12 分钟',
      skill: '锻炼空间走线',
    },
    puzzle: {
      tagline: '只有一种布局获胜。找出它。',
      bullet1: '10 个关卡,唯一解',
      bullet2: '预算与波次固定',
      bullet3: '视野遮挡墙 + 塔位上限',
      length: '每关约 2 分钟',
      skill: '锻炼推理',
    },
  },
  difficulty: {
    easy: '简单',
    normal: '普通',
    hard: '困难',
  },
  difficultySub: {
    easy: '轻松',
    normal: '平衡',
    hard: '✦ {cost}',
  },

  overlay: {
    paused: '已暂停',
    pauseHint: '按  空格  继续',

    missionComplete: '// 任务完成',
    gridSecured: '电网守住',
    wavesRepelled: '全部 {total} 波突袭已击退。',
    continueEndless: '继续  →  无尽',
    unlockEndless: '在档案中解锁无尽以继续',
    restartCampaign: '或按  R  重启战役',

    systemFailure: '// 系统故障',
    coreBreached: '核心被攻破',
    endlessEnded: '无尽局结束于第 {wave} 波。',
    reactorOverrun: '反应堆在第 {wave} / {total} 波被攻陷。',
    runItBack: '按  R  再来一局',

    finalScore: '最终得分',
    newHighScore: '★  新纪录  ★',
    newEndlessBest: '★  新的无尽纪录  ★',
    highScore: '最高分',
    endlessBest: '无尽纪录',

    // Sprint 69 — top-banner countdown overlays drawn by render.ts.
    aftershock: "// 余震 — 电网偿还  {sec}s //",
    overcharge: "// OVERCHARGE — 电网增益  {sec}s //",
    brownout: "// 电网衰退 — 塔被限制 //",
  },

  profile: {
    title: '档案',
    subtitle: '// 元进度',
    closeHint: '按  P  或  ESC  关闭',
    tabUnlocks: '解锁',
    tabScoreboard: '排行榜',
    flux: 'FLUX',
    unlockTree: '解锁树',
    unlockedCount: '{owned} / {total} 已解锁',
    lifetime: '累计',

    branchArsenal: '军火库',
    branchModes: '模式',
    branchChallenge: '挑战',
    branchCosmetic: '外观',

    statRunsPlayed: '游玩局数',
    statCampaignWins: '战役胜利',
    statBestScore: '最高得分',
    statDeepestEndless: '最深无尽',
    statTotalFlux: '累计 FLUX',

    personalBests: '个人最佳',
    waveLabel: '第 {wave} 波',

    runHistory: '对局历史',
    noRuns: '还没有对局 — 完成一局开始登顶之路。',
    earlierRuns: '+ {n} 局更早的对局',
    earlierRunsPlural: '+ {n} 局更早的对局',
    colRun: '对局',
    colMode: '模式 · 难度',
    colResult: '结果',
    colOverload: 'OVL',
    colScore: '得分',
    colDeltaBest: 'Δ 最佳',
    colFlux: 'FLUX',
    starBest: '★ 最佳',

    statusUnlocked: '已解锁',
    statusOwned: '已拥有',
    statusRequiresPrereq: '需先 {prereq}',
    statusFluxShort: '还差 ✦{short} Flux',
    statusAffordable: '可负担 — 点击解锁',
    statusCost: '✦{cost} 解锁',
    buyAction: '购买  ▸',
    nodeReq: '前置:{prereq}',

    profileLauncher: '档案',
    profileKeyHint: '[ P ]',
    viewScoreboard: '查看排行榜',
    newPersonalBest: '★  新的个人最佳  ★',
    fromYourBest: '相比你的最佳',

    grantTower: '武器塔',
    grantAbility: '主动技能',
    grantMode: '游戏模式',
    grantDifficulty: '难度',
    grantOverload: '过载 等级 {tier}',
    grantTheme: '场地主题',

    // Sprint 69 — unlock-tree hover preview.
    nodePreview: {
      towerMortar: "间接火力。放在路径后方 — 近距离死区；炮弹砸群组，削减护甲。",
      towerSniper: "视线穿透矛。瞄准长直线 — 线上每个敌人都受击；拐角弱。",
      abilityAirstrike: "在目标格上一次性爆炸（45c）。清理一群或削弱硬目标。",
      abilityEmp: "全场眩晕（80c）。当道被淹没时冻结所有敌人片刻。",
      modeEndless: "超过第 10 波 — 程序波持续直到核心倒下。新的刷分赛道。",
      modeMaze: "开放场地建路模式。塔阻挡其格，敌人 A*-绕行。",
      difficultyHard: "更难的波；更大的金币和分数乘数。解锁高 Flux 段。",
      overloadCritical: "Overload 3 级 — 加大火力以获得更肥的下波分数/Flux 乘数。",
      overloadMeltdown: "Overload 4 级 — 最难的预设波。顶级乘数；只适合稳健阵容。",
      themeAmber: "美化调色板 — 暖色 amber CRT 终端。纯美化；无玩法效果。",
      themePhosphor: "美化调色板 — 复古荧光绿。纯美化；无玩法效果。",
    },
  },

  help: {
    eyebrow: '/ /  参 考',
    title: '操作手册',
    controls: '操作',
    powerGrid: '电网系统',
    powerGridBody:
      '武器消耗电力;Pylon 提供电力。当需求超过供应,电网将断电,所有塔同时减速 — 建造 Pylon 以维持火力。',
    objective: '目标',
    objectiveBody:
      '清掉全部 15 波战役以获胜;核心生命归零即失败。无尽模式将无限循环。',
    mazeMode: '迷宫模式',
    mazeModeBody:
      '第三种模式,开放场地:塔占据并阻挡它所在的格子,敌人用 A* 绕路,所以你来搭迷宫。不可将核心完全围死。',
    glitches: '故障',
    towers: '塔',
    abilities: '技能',
    replayIntro: '↺  回放介绍',
    closeHint: 'Esc / H / ? 或点击任意位置关闭',
  },

  intro: {
    eyebrow: '/ /  系 统 简 报',
    titleBig: 'OVERVOLT',
    subtitle: '守护核心',
    line1: '被腐蚀的程序沿着电路向你的核心进军。建塔在它们突破之前摧毁它们。',
    line2:
      '⚡ 每座塔都消耗电力。过度建造会让电网断电 — 所有塔会同时减速。建造 Pylon 保持电力。',
    line3: '在备战阶段建造,然后按 开始波次。挺过 15 波即获胜。',
    line4: '鼠标建造 · 1–5 选择 · 空格 开始/暂停 · U 升级 · S 出售',
    gotIt: '明白  →',
    gotItHint: '按任意键继续',
  },

  puzzle: {
    label: '解谜',
    levels: {
      'p0-first-light': {
        name: '初光',
        hint: '在通道旁放一座 Pulse。射程内任意格子皆可。',
      },
      'p1-long-lance': {
        name: '长枪',
        hint: '一座对齐线路的 Sniper 可穿透每个敌人。',
      },
      'p2-over-the-wall': {
        name: '越墙',
        hint: '墙挡住所有直射光束。迫击弹可弧线越过。',
      },
      'p3-funhouse-mirror': {
        name: '哈哈镜',
        hint: '穿过缝隙的 Sniper 可同时打中两条线。',
      },
      'p4-surplus-riches': {
        name: '盈余之富',
        hint: '在自由带之外多建 Pylon。Arc Coil 靠富余成长。',
      },
      'p5-pinch-point': {
        name: '挤压点',
        hint: 'Cryo 画上挤压点;周围的 Pulse 收尾整群。',
      },
      'p6-cipher-gauntlet': {
        name: 'Cipher 试炼',
        hint: '迫击撕开护甲;Cryo 画弯道;Sniper 穿透下方支腿。整套兵种全用上。',
      },
      'p7-kernel-echo': {
        name: 'Kernel 回声',
        hint: '迫击越过视野墙投射。Sniper 自下方穿入。注意 Power Drain。',
      },
      'p8-overclock-crunch': {
        name: '超频压力',
        hint: '一座 Pulse 跟不上。在爆发窗口超频 — 持续射速不够。',
      },
      'p9-pylon-pinch': {
        name: 'Pylon 挤压',
        hint: '把 Pylon 塞在你的塔之间 — 光环上限奖励紧凑布局。',
      },
    },
    briefing: {
      eyebrow: "谜题简报",
      startBtn: "开始 ▷",
      cancelBtn: "取消",
      sectionConstraints: "约束",
      sectionTowers: "塔",
      sectionWinCondition: "胜利条件",
      budgetLabel: "金币预算",
      wavesLabel: "波数",
      towerAllowed: "允许",
      towerForbidden: "锁定",
      resultEyebrowWon: "谜题完成",
      resultEyebrowLost: "谜题失败",
      resultTitleWon: "解开！",
      resultTitleLost: "再试一次",
      resultStatTowers: "塔",
      resultStatGold: "花费金币",
      resultStatLives: "剩余生命",
      resultNextBtn: "下一关 ▷",
      resultReplayBtn: "重玩",
      resultResetBtn: "重置尝试",
      resultBackBtn: "返回选择",
    },

    // Sprint 69 — puzzle level-select overlay chrome.
    select: {
      subtitle: "手作逻辑关 — 推理出唯一获胜布局。",
      close: "关闭",
      cardTitle: 'P{index}  {name}',
      lockedCard: "🔒 已锁",
      solved: "✓ 已解",
      unsolved: "▷ 未解",
      noSolve: "尚未解开",
      bestLine: "最佳：{towers}t · {gold}g · {power}p",
      allowed: "允许：{list}",
      parPlaceholder: "基准：— / — / —",
      parRow: "{towers}t · {gold}g · {power}p",
    },
  },

  localeChip:
 {
    label: '语言',
    en: 'EN',
    cs: 'CS',
  },

  resume: {
    title: '继续之前的对局?',
    resume: '继续',
    newRun: '新对局',
    hint: '[Enter] 继续   ·   [Esc] 新',
    detail: "{mode} · {difficulty} · 第 {wave} 波 · {towers} 座塔 · {gold}g · {lives}♥",
  },

  // -------- Sprint 69: Kernel boss UI chrome ---------------------------------
  boss: {
    kernelHeader: "⚠  内核",
    phase: "阶段 {phase} · {name}",
    powerDrain: "⚡ 电力抽取",
    kernelIncoming: "⚠  内核  来袭  ⚠",
    phaseName: {
      boot: "BOOT SEQUENCE",
      fork: "FORK BOMB",
      panic: "KERNEL PANIC",
    },
  },

  // -------- Sprint 69: Map registry display strings --------------------------
  maps: {
    default: { label: "Datawash", tagline: "开放 S 弯 — 经典 OVERVOLT 布局。" },
    cramped: { label: "Switchyard", tagline: "狭窄通道 — 每格都重要。" },
    lines: { label: "Long Lines", tagline: "长直线 — 引导矛锋。" },
    serpentine: { label: "Snake Run", tagline: "紧致蛇形 — Cryo 涂绘大放异彩。" },
  },

  // -------- Sprint 69: Ability display names ---------------------------------
  abilities: {
    overcharge: { label: 'Overcharge' },
    airstrike: { label: 'Airstrike' },
    empBurst: { label: 'EMP Burst' },
  },

  // -------- Sprint 69: Overload tier names -----------------------------------
  overload: {
    tierName: {
      nominal: "标称",
      charged: "已充能",
      surging: "激增",
      critical: "临界",
      meltdown: "熔毁",
    },
  },

  // -------- Sprint 69: Endless modifier labels -------------------------------
  endlessMod: {
    armoredSurge: { label: "装甲突击" },
    fastPack: { label: "快速群组" },
    scatteredSpread: { label: "分散" },
    streakRunners: { label: "Streak 跑者" },
    daemonRush: { label: "Daemon 冲刺" },
  },

  // -------- Sprint 69: Enemy display names (Help roster) ---------------------
  enemies: {
    bit: { label: "Bit" },
    packet: { label: "Packet" },
    daemon: { label: "Daemon" },
    fork: { label: "Fork" },
    cipher: { label: "Cipher" },
    patch: { label: "Patch" },
    buffer: { label: "Buffer" },
    streak: { label: "Streak" },
    kernel: { label: "内核" },
  },

  // -------- Sprint 69: Help-panel enemy one-liners ---------------------------
  enemyRole: {
    bit: "基础杂兵 — 便宜、慢、脆弱。",
    packet: "快而脆；冲向核心。",
    daemon: "重型 HP 海绵；漏过后伤害大。",
    fork: "死亡时分裂成两个 Bit。",
    cipher: "装甲 — 用 Mortar 腐蚀撕裂。",
    patch: "治疗附近 Glitch；优先击破。",
    buffer: "再生护盾；用爆发击穿。",
    streak: "单独时冲刺；抗减速。覆盖路径。",
    kernel: "阶段 Boss — 抽走能量。下个阶段前爆发击败。",
  },

  // -------- Sprint 69: Help-panel ability one-liners -------------------------
  abilityRole: {
    overcharge: "推动电网：所有塔短时间内更快开火。围绕硬波规划。",
    airstrike: "在目标格上一次性爆炸。清理一群或削弱大目标。",
    empBurst: "全场眩晕 — 所有敌人短暂冻结。当道被淹没时争取时间。",
  },

  // -------- Sprint 69: Tooltip chrome ----------------------------------------
  tooltip: {
    levelOf: "LV {level}/{max}",
    credits: "{cost} 信用",
    next: "下一级  →  LV {level}   {cost} 信用",
    cooldown: "冷却  {sec}s",
    stat: {
      supply: "供能  +{n}w 给电网",
      dmgRngCd: "DMG {damage}    RNG {range}    CD {cd}s",
      pwrChain: "PWR {power}w    链 {chain}",
      pwrAoe: "PWR {power}w    AOE {targets} 目标",
      slow: "SLOW ×{factor}  持续  {sec}s",
      brittle: "脆弱 +{pct}% 来自其他塔伤害",
      pwrBlast: "PWR {power}w    爆破 {tiles} 格",
      pwrSingle: "PWR {power}w    1 目标",
    },
  },

  // -------- Sprint 69: Help-panel control-row labels -------------------------
  controls: {
    arm: "选取 Pulse / Arc / Pylon / Cryo / Mortar / Sniper",
    mouse: "在格上放置 · 点击塔选中",
    space: "启动波次 · 暂停波次",
    fast: "快进 (1× ⇄ 2×)",
    upgradeSell: "升级 / 出售所选塔",
    target: "切换所选塔的目标模式",
    profile: "打开档案（解锁树·得分榜）",
    utility: "取消 · 静音 · 重启游戏",
  },

  // -------- Sprint 69: Unlock-tree node display names ------------------------
  unlocks: {
    towerMortar: { name: "Splash Mortar" },
    towerSniper: { name: "Sniper Rail" },
    abilityAirstrike: { name: "Airstrike" },
    abilityEmp: { name: "EMP Burst" },
    modeEndless: { name: "无尽模式" },
    modeMaze: { name: "Maze 模式" },
    difficultyHard: { name: "困难难度" },
    overloadCritical: { name: "Overload：临界" },
    overloadMeltdown: { name: "Overload：熔毁" },
    themeAmber: { name: "Amber 终端" },
    themePhosphor: { name: "荧光绿" },
  },

  // Sprint 73 — 互动教程 (docs/TUTORIAL_DESIGN.md §6).
  tutorial: {
    button: {
      start: '开始教程',
      startSub: '第一次玩？开始导览 →',
      replay: '重新导览',
      replaySub: '复习基础',
    },
    step: {
      welcome: {
        title: '欢迎来到 OVERVOLT',
        body: '你是一台失败反应堆的防御 AI。损坏程序沿着电路向你的 Core 进军——在它们突破之前摧毁它们。',
      },
      path: {
        title: '路径',
        body: '敌人始终沿着这条 S 型曲线行进，从左侧的出生点到右侧的 Core。你决定在哪里布置炮塔。',
      },
      firstPlace: {
        title: '放置首座炮塔',
        body: '一台 Pulse Turret 需要 50 信用、4 电力。点击路径旁发光的格子放下一座。',
      },
      wave1: {
        title: '发起第一波',
        body: '按 开始波次。你的 Pulse 会自动射击范围内任何目标。',
      },
      towerRoles: {
        title: '五座炮塔，五种角色',
        body: 'Pulse：随处可靠。Sniper：长直线。Mortar：间接火力，远离路径。Arc：随电力盈余增强。Cryo：在一个瓶颈处绘制减速与脆弱。',
      },
      powerGrid: {
        title: '电力网络',
        body: '每座炮塔都消耗电力。当需求超过供应时电网会陷入崩溃，所有炮塔都会减速。建造 Pylon——相邻的 Pylon 会提升射速。',
      },
      overloadDial: {
        title: 'Overload — 风险与回报',
        body: '为下一波转高表盘：更强的敌人、更多 Flux。转低则稳妥。',
      },
      overclock: {
        title: '炮塔超频',
        body: '在选中的炮塔上启用 OVERCLOCK 获得爆发窗口：射速更快、能耗更高。爆发胜利，持久败北。',
      },
      abilities: {
        title: '主动技能',
        body: '三个槽位：Overcharge 给整个电网增益。Airstrike 投下持续焦烧区。EMP Burst 眩晕全场。规划使用——别慌乱按键。',
      },
      mazePreview: {
        title: 'Maze 模式',
        body: '没有固定路径——炮塔阻挡所在格子，敌人 A* 绕行。你来构建他们必须走的路线。',
      },
      puzzlePreview: {
        title: 'Puzzle 模式',
        body: '手工设计的逻辑关卡，配视野墙体。预算固定、波次固定——找出唯一获胜布局。完全不同的玩法。',
      },
      wrapUp: {
        title: '你已就绪',
        body: '通关获得 FLUX——在个人档案中花费它解锁新炮塔、技能和模式。先玩 Campaign；之后回来挑战 Endless、Maze 和 Puzzle。',
      },
    },
    ui: {
      eyebrow: '第 {n} / {total} 步',
      next: '下一步 →',
      back: '← 返回',
      skip: '跳过导览',
      play: '游玩  ▸',
      skipConfirm: '跳过导览？你可以随时从标题画面重新播放。',
    },
  },
};
